/**
 * The user's starred destinations and the sections between them — the rail in
 * a browser tab.
 *
 * One key in the settings store, holding a JSON object keyed by scope. `root`
 * is the browser rail's destination ids; `rootLayout` is the same rail with
 * section headers (spec 2026-09-16-nav-rail-sections-design.md §1.1). `root`
 * stays a plain id array because a tab still running pre-sections code drops
 * anything else in it — its writes change `root` only, and the layout is
 * reconciled against `root` on read.
 *
 * Writes merge into the value the SERVER holds, through
 * `settingsService.updateSetting`, one at a time. Optimistic: the slice updates
 * first so the rail moves on click; a failed write reverts and reports to the
 * status line.
 */
import { useCallback, useMemo } from 'react';

import { useAppStore } from '@/stores/useAppStore';
import settingsService from '@shared/services/settings';
import { clearSource, reportFailed } from '@shared/status/saveStatusStore';

import { DEFAULT_STAR_IDS } from './destinations';
import { cleanIds, reconcileLayout, starsOf, type RailLayout } from './railLayout';
import { toggleStar } from './railLayoutOps';

export const STARS_SETTING_KEY = 'nav.stars';
const DESCRIPTION = 'Starred sidebar destinations';

/**
 * Status-line source id for star writes. One id for all of them: the rail is a
 * single value, so a second failed write replaces the first rather than
 * stacking another row the user must dismiss.
 */
const STATUS_SOURCE = 'nav-stars';

/**
 * What the column holds: a partial map. An absent key resolves to defaults at
 * read time — defaults are never written back. Unknown keys ride along untouched.
 */
type PersistedStars = Record<string, unknown>;

/** The keys one rail write replaces. */
interface RootPatch {
  root: string[] | undefined;
  rootLayout: RailLayout | undefined;
}

/**
 * The persisted value, normalized: a legacy bare array is the root set; an
 * object is kept as-is; garbage is empty. No defaults are filled in here.
 */
function parseStarsValue(raw: string | undefined): PersistedStars {
  if (raw === undefined) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed)) return { root: parsed };
    if (parsed !== null && typeof parsed === 'object') return parsed as PersistedStars;
    return {};
  } catch {
    return {};
  }
}

/** The root ids as shown: the stored set cleaned, or the defaults when nothing is stored. */
function resolveRoot(persisted: PersistedStars): string[] {
  const stored = persisted.root;
  return Array.isArray(stored) ? cleanIds(stored) : [...DEFAULT_STAR_IDS];
}

/** The rail layout as shown: `rootLayout` reconciled against the root ids. */
function resolveLayout(persisted: PersistedStars): RailLayout {
  return reconcileLayout(persisted.rootLayout, resolveRoot(persisted));
}

/** The layout currently in the slice. */
function currentLayout(): RailLayout {
  return resolveLayout(parseStarsValue(useAppStore.getState().settings[STARS_SETTING_KEY]));
}

/** `persisted` with the root keys replaced (a key is removed when its value is undefined). */
function withRoot(persisted: PersistedStars, patch: RootPatch): PersistedStars {
  const next: PersistedStars = { ...persisted };
  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined) delete next[key];
    else next[key] = value;
  }
  return next;
}

/** The root keys `persisted` holds now, for a revert. */
function rootOf(persisted: PersistedStars): RootPatch {
  return {
    root: persisted.root as string[] | undefined,
    rootLayout: persisted.rootLayout as RailLayout | undefined,
  };
}

/** What the server holds for this key right now; `undefined` when nothing is stored. */
async function readServerValue(): Promise<string | undefined> {
  const settings = await settingsService.getAllSettings();
  return settings.find(s => s.key === STARS_SETTING_KEY)?.value;
}

/**
 * One writer at a time, and only the newest layout is ever sent. Creating a
 * section and renaming it are two writes milliseconds apart; every write
 * carries the whole layout, so an intermediate one is redundant once a newer
 * one exists.
 *
 * - `pending` is the newest requested layout not yet sent; `drain` picks it up.
 * - `confirmed` is what the server last accepted (seeded from the slice when
 *   the queue is idle). A failure reverts to it — never to a queued write's
 *   optimistic value, which the server never held.
 * - `requestSeq` counts requests, so a Retry for a write the user has since
 *   superseded does nothing instead of replaying an older layout.
 */
let pending: RailLayout | null = null;
let confirmed: PersistedStars = {};
let requestSeq = 0;
let draining: Promise<void> | null = null;

/** The value to show while `serialized` is on the server and `pending` may still be queued. */
function shownAfterWrite(serialized: string): string {
  if (pending === null) return serialized;
  return JSON.stringify(withRoot(parseStarsValue(serialized), patchOf(pending)));
}

/** The root keys a layout writes: `root` is its destination ids, for clients that predate sections. */
function patchOf(layout: RailLayout): RootPatch {
  return { root: starsOf(layout), rootLayout: layout };
}

/** Put the last confirmed root keys back in the slice, keeping any sibling scope the slice holds. */
function revertToConfirmed(): void {
  const current = parseStarsValue(useAppStore.getState().settings[STARS_SETTING_KEY]);
  const reverted = withRoot(current, rootOf(confirmed));
  useAppStore.getState().setSetting(STARS_SETTING_KEY, JSON.stringify(reverted));
}

/**
 * Send one layout, merged into the value the SERVER holds — an installed app
 * stays open for days with a stale snapshot, and a whole-object write from it
 * would erase a sibling scope curated elsewhere.
 *
 * `updateSetting` notifies the store's settings listener with the value it
 * wrote, which would briefly show THIS layout over a newer queued one; the
 * slice is set right after so the newest edit stays on screen.
 *
 * The failure has to be *visible*: a server that once rejected every write for
 * a day left the rail silently on its defaults, because a reverted optimistic
 * update is indistinguishable from never having clicked.
 */
async function sendLayout(layout: RailLayout, seq: number): Promise<void> {
  try {
    const merged = withRoot(parseStarsValue(await readServerValue()), patchOf(layout));
    const serialized = JSON.stringify(merged);
    await settingsService.updateSetting(STARS_SETTING_KEY, serialized, DESCRIPTION);
    confirmed = merged;
    useAppStore.getState().setSetting(STARS_SETTING_KEY, shownAfterWrite(serialized));
    clearSource(STATUS_SOURCE);
  } catch (error) {
    console.warn('[nav.stars] write failed', error);
    // A newer layout is queued: it carries this change and reports its own outcome.
    if (pending !== null) return;
    revertToConfirmed();
    const retry = () => (seq === requestSeq ? persistLayout(layout) : Promise.resolve());
    reportFailed(STATUS_SOURCE, 'Starred items', retry);
  }
}

/** Send queued layouts until none is left. Clears `draining` in the same tick the queue empties. */
async function drain(): Promise<void> {
  try {
    while (pending !== null) {
      const layout = pending;
      pending = null;
      await sendLayout(layout, requestSeq);
    }
  } finally {
    draining = null;
  }
}

/**
 * Persist a layout: optimistically in the slice so the rail moves at once, then
 * queued for the server. Resolves when the queue has drained.
 */
function persistLayout(layout: RailLayout): Promise<void> {
  requestSeq += 1;
  const store = useAppStore.getState();
  const current = parseStarsValue(store.settings[STARS_SETTING_KEY]);
  // Idle queue: the slice mirrors the server, so it is the revert target.
  if (draining === null) confirmed = current;
  store.setSetting(STARS_SETTING_KEY, JSON.stringify(withRoot(current, patchOf(layout))));
  pending = layout;
  draining ??= drain();
  return draining;
}

/** Read the rail's stars and sections; toggle a star; write a whole layout. */
export function useStars() {
  const raw = useAppStore(s => s.settings[STARS_SETTING_KEY]);
  const isSettingsLoaded = useAppStore(s => s.isSettingsLoaded);
  const layout = useMemo(() => resolveLayout(parseStarsValue(raw)), [raw]);
  const starredIds = useMemo(() => starsOf(layout), [layout]);

  const isStarred = useCallback((id: string) => starredIds.includes(id), [starredIds]);

  /** Every mutation goes through one write; refuses before settings have loaded. */
  const write = useCallback(
    async (next: RailLayout) => {
      if (!isSettingsLoaded) {
        console.warn('[nav.stars] settings not loaded; ignoring write');
        return;
      }
      await persistLayout(reconcileLayout(next, cleanIds(starsOf(next))));
    },
    [isSettingsLoaded]
  );

  /**
   * Add (to the end) or remove one destination. Reads the live layout rather
   * than the closed-over one, so two toggles fired before a re-render both apply.
   */
  const toggle = useCallback((id: string) => write(toggleStar(currentLayout(), id)), [write]);

  /** Replace the whole layout — every section and row operation ends here. */
  const setLayout = useCallback((next: RailLayout) => write([...next]), [write]);

  return { starredIds, layout, isStarred, toggle, setLayout };
}
